import {
  nonFilterParamKeys,
  NonFilterParamKeys,
  NonFilterParams,
  SearchFilters,
  validUrlSegmentFilterKeys,
  ValidUrlSegmentSearchFilterKeys,
} from '@retail-common/util-search-param-types'
import {
  addNonFilterParamToUrlParams,
  AddNonFilterParamToUrlParamsConfig,
} from '../add-non-filter-param-to-url-params/add-non-filter-param-to-url-params'
import { convertFilterValueToUrlSegment } from '../convert-filter-value-to-url-segment/convert-filter-value-to-url-segment'

function isNonFilterParamKey(key: string): key is NonFilterParamKeys {
  return nonFilterParamKeys.includes(key as NonFilterParamKeys)
}

export function isValidSegmentFilterKey(
  key: string,
): key is ValidUrlSegmentSearchFilterKeys {
  return validUrlSegmentFilterKeys.includes(
    key as ValidUrlSegmentSearchFilterKeys,
  )
}

function isStringArray(value: number | boolean | string[]): value is string[] {
  return Array.isArray(value)
}

export const filterToMapReducer =
  (addNonFilterParamToUrlParamsConfig: AddNonFilterParamToUrlParamsConfig) =>
  (
    filters: Partial<SearchFilters & NonFilterParams>,
    {
      urlPathMap,
      urlSearchParams,
    }: {
      urlPathMap: Map<
        string,
        { searchApiValue: string; urlSegmentValue: string }
      >
      urlSearchParams: URLSearchParams
    },
    [key, value]: [string, string[] | number],
  ) => {
    if (isNonFilterParamKey(key)) {
      // special handling of non-filter params, don't include them in the url when the value is the default
      addNonFilterParamToUrlParams(
        key,
        value as NonFilterParams[NonFilterParamKeys],
        urlSearchParams,
        addNonFilterParamToUrlParamsConfig,
      )
      return { urlPathMap, urlSearchParams }
    }

    if (!isStringArray(value)) {
      console.warn(
        `Unexpected type value for key '${key}', expected string[] but got ${typeof value}`,
      )

      return { urlPathMap, urlSearchParams }
    }

    //#region Special handling for year filters
    if ((key === 'fromYear' || key === 'toYear') && value.length === 1) {
      const otherYearKey = key === 'fromYear' ? 'toYear' : 'fromYear'
      const otherYearValue =
        filters[otherYearKey as keyof (SearchFilters & NonFilterParams)]

      // If years are different or other year is not set, use query params for years
      if (
        otherYearValue === undefined ||
        !isStringArray(otherYearValue) ||
        otherYearValue.length !== 1 ||
        otherYearValue[0] !== value[0]
      ) {
        urlSearchParams.set(key, value.join(','))
        return { urlPathMap, urlSearchParams }
      }

      // If we reach here, the years are the same
      // Only process this for fromYear to avoid duplication
      if (key === 'fromYear') {
        urlPathMap.set('vehicleYear', {
          searchApiValue: `${value[0]}-year`,
          urlSegmentValue: value[0],
        })
      }
      // Skip adding to query params since we're adding as a segment
      return { urlPathMap, urlSearchParams }
    }
    //#endregion

    // when all possible stores are selected, show 'all-stores' in the URL params rather than listing them all
    // if (
    //   key === 'store' &&
    //   allCinchStores.length > 0 &&
    //   value.length === allCinchStores.length &&
    //   allCinchStores.every((storeValue) => value.includes(storeValue))
    // ) {
    //   urlSearchParams.set(key, 'all-stores')
    //} else
    if (value.length === 1 && isValidSegmentFilterKey(key)) {
      // single value filters that are allowed in the URL segments should be added to the urlPathMap
      urlPathMap.set(key, {
        searchApiValue: convertFilterValueToUrlSegment(key, value[0]),
        urlSegmentValue: value[0],
      })
    } else if (
      value.length > 1 ||
      (value.length === 1 && !isValidSegmentFilterKey(key))
    ) {
      // multi-value filters and those not allowed in URL segments are added to the search params
      urlSearchParams.set(key, value.join(','))
    }
    return { urlPathMap, urlSearchParams }
  }
